import { useTranslator } from '@picsart/rc/services/localization';

import SearchRecent from '@/components/SearchRecent';
import SearchEmpty from '@/components/SearchEmpty';

import useStyles from './styles';
import type { SearchBodyProps } from './types';

/**
 * The state switch under the search field — recent searches, a message, or the
 * results. Shared so the mobile screen and the desktop dropdown can never
 * drift into showing different things for the same query.
 *
 * The results themselves come in as `children` (see `SearchBodyProps`), which
 * is also what keeps the mobile card grid out of the desktop entry bundle.
 */
const SearchBody = ({
  children,
  isCompact,
  query,
  recentSearches,
  restrictionMessage,
  isFetching,
  isError,
  hasNoResults,
  onRecentSelect,
  onRecentRemove,
  onRecentClearAll,
  onRetry,
}: SearchBodyProps) => {
  const classes = useStyles();
  const translate = useTranslator();

  // Idle — nothing searched yet. Offer the user's own history rather than a
  // blank screen; `SearchRecent` renders nothing when there is no history.
  if (!query) {
    return (
      <SearchRecent
        terms={recentSearches}
        onSelect={onRecentSelect}
        onRemove={onRecentRemove}
        onClearAll={onRecentClearAll}
      />
    );
  }

  // Loading shows the results in skeleton mode, so the placeholders match the
  // real layout instead of a centred spinner that the results then displace.
  if (isFetching) return children;

  if (restrictionMessage) {
    return <p className={classes.message}>{restrictionMessage}</p>;
  }

  if (isError) {
    return (
      <SearchEmpty
        title={translate('Something went wrong')}
        description={translate('Check your connection and try again.')}
        isCompact={isCompact}
        onRetry={onRetry}
      />
    );
  }

  if (hasNoResults) {
    return (
      <SearchEmpty
        title={translate('Sorry, we can’t find what you’re looking for.')}
        description={translate('Please try something else.')}
        isCompact={isCompact}
      />
    );
  }

  return children;
};

export default SearchBody;
